import React, { useState, useEffect, useContext, useRef } from 'react';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { PRIORITY_OPTIONS } from './priority';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { AlertTriangle, Wrench, MapPin, Check } from 'lucide-react';
import { useFieldErrors, FieldError, CorrectionSummary } from './FormValidation';

// Service Request form. Two modes:
//   mode="client"  → Location Requestor: NO customer dropdown, customer/locations
//                    come from the scoped /context/client endpoint.
//   mode="proxy"   → TFS Administrator creating on behalf of a client: customer
//                    dropdown available, filters locations.
const ServiceRequestForm = ({ onSubmitted, onCancel, mode = 'proxy', activeLocationId = null, lockedCustomer = null, prefill = null, fdMode = false, createWorkOrder = false, onWorkOrderCreated = null }) => {
  const { token, user } = useContext(AuthContext);
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
  const currentUserName = user?.name;
  const isClient = mode === 'client';
  const locked = !isClient && !!lockedCustomer;
  const canRoute = fdMode || !isClient;          // FD self-service or TFS proxy choose the handling/route
  const isLocationRequester = isClient && !fdMode;

  const [customers, setCustomers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [areas, setAreas] = useState([]);
  const [addingArea, setAddingArea] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const idemKeyRef = useRef(`wo-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);
  const summaryRef = useRef(null);
  const scrollToSummary = () => window.requestAnimationFrame(() =>
    summaryRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }));
  const [clientCustomerName, setClientCustomerName] = useState('');

  const [form, setForm] = useState({
    customer_id: lockedCustomer?.id || '', customer_name: lockedCustomer?.name || '',
    location_id: prefill?.location_id || '', location_name: prefill?.location_name || '',
    area_id: '', area_name: '', new_area_name: '',
    title: prefill?.title || '', description: prefill?.description || '', request_type: 'Standard', priority_level: '',
    service_scope: '', fulfillment_route: (fdMode || mode !== 'client') ? 'TFS Operations' : '',
    execution_authorization: '', authorization_method: '', authorized_by_email: '', authorized_nte_amount: '', authorization_evidence_ref: '',
    // Intake provenance (proxy) + optional client references (both modes)
    intake_source: 'Direct Client Request', source_system: '',
    original_request_date: '', received_by_tfs_date: '',
    client_reference_number: '', client_po_number: '', client_work_order_number: '',
    client_required_completion_date: '',
    requester_contact_name: '', requester_contact_title: '', requester_contact_email: '', requester_contact_phone: '',
    client_authorized_by: '', client_authorized_by_role: '',
  });

  const fe = useFieldErrors();
  const FIELD_LABELS = {
    customer_id: 'Customer', location_id: 'Service Location', area: 'Service Area / Suite',
    title: 'Title', description: 'Description', service_scope: 'Service Classification',
    priority_level: 'Operational Priority', execution_authorization: 'Authorization',
    authorized_nte_amount: 'NTE Amount', original_request_date: 'Original Request Date & Time',
    received_by_tfs_date: 'Received by TFS Date & Time', client_required_completion_date: 'Client-Required Completion Date',
  };
  const set = (patch) => {
    setForm((f) => ({ ...f, ...patch }));
    // Clear a field's error only once it actually has a value — resetting a field to empty
    // (e.g. changing customer clears its location) must NOT dismiss that field's error.
    Object.entries(patch).forEach(([k, v]) => { if (v !== '' && v != null) fe.clearError(k); });
    if ((('area_id' in patch) && patch.area_id) || (('new_area_name' in patch) && (patch.new_area_name || '').trim())) fe.clearError('area');
  };
  const [contacts, setContacts] = useState([]);
  const [selectedContactId, setSelectedContactId] = useState('');
  const [selectedAuthorizerId, setSelectedAuthorizerId] = useState('');

  // CLIENT MODE: load scoped context (own customer + assigned locations only)
  useEffect(() => {
    if (!isClient) return;
    axios.get(`${API}/service-requests/context/client`, authHeaders)
      .then((r) => {
        const ctx = r.data || {};
        const cust = ctx.customer || {};
        const locs = ctx.locations || [];
        setClientCustomerName(cust.name || '');
        setLocations(locs);
        const patch = { customer_id: cust.id || '', customer_name: cust.name || '' };
        // Preselect the Dashboard's active location if authorized, else auto-select a lone location.
        const pre = (activeLocationId && locs.find((l) => l.id === activeLocationId)) || (locs.length === 1 ? locs[0] : null);
        if (pre) { patch.location_id = pre.id; patch.location_name = pre.location_name; }
        set(patch);
      })
      .catch(() => toast.error('Failed to load your account context'));
  }, [isClient, activeLocationId]);

  // PROXY MODE: load all customers (TFS internal)
  useEffect(() => {
    if (isClient) return;
    axios.get(`${API}/customers`, authHeaders)
      .then((r) => setCustomers(r.data || []))
      .catch(() => toast.error('Failed to load customers'));
  }, [isClient]);

  // PROXY MODE: dependent locations when customer changes
  useEffect(() => {
    if (isClient || !form.customer_id) { if (!isClient) setLocations([]); return; }
    axios.get(`${API}/service-locations`, authHeaders)
      .then((r) => setLocations((r.data || []).filter((l) => l.customer_id === form.customer_id)))
      .catch(() => toast.error('Failed to load locations'));
  }, [form.customer_id, isClient]);

  // PROXY MODE: selectable customer contacts (Main Contact + client users) for the requester picker
  useEffect(() => {
    if (isClient || !form.customer_id) { setContacts([]); setSelectedContactId(''); return; }
    axios.get(`${API}/customers/${form.customer_id}/contacts`, authHeaders)
      .then((r) => setContacts(r.data?.contacts || []))
      .catch(() => setContacts([]));
  }, [form.customer_id, isClient]);

  // Areas depend on location — fetch only ACTIVE areas assigned to the selected location.
  useEffect(() => {
    if (!form.location_id) { setAreas([]); return; }
    axios.get(`${API}/building-areas/location/${form.location_id}`, authHeaders)
      .then((r) => setAreas(r.data || []))
      .catch(() => setAreas([]));
  }, [form.location_id]);

  const onCustomer = (id) => {
    const c = customers.find((x) => x.id === id);
    setSelectedContactId(''); setSelectedAuthorizerId('');
    set({ customer_id: id, customer_name: c?.name || '', location_id: '', location_name: '', area_id: '', area_name: '' });
  };
  const onPickContact = (id) => {
    setSelectedContactId(id);
    const c = contacts.find((x) => x.id === id);
    if (!c) return;
    set({ requester_contact_name: c.name || '', requester_contact_title: c.title || '', requester_contact_email: c.email || '', requester_contact_phone: c.phone || '' });
  };
  const onPickAuthorizer = (id) => {
    setSelectedAuthorizerId(id);
    const c = contacts.find((x) => x.id === id);
    if (!c) return;
    // Use the actual selected person's Title/Position — never default to the Main Contact
    set({ client_authorized_by: c.name || '', client_authorized_by_role: c.title || '' });
  };
  const onLocation = (id) => {
    const l = locations.find((x) => x.id === id);
    set({ location_id: id, location_name: l?.location_name || '', area_id: '', area_name: '', new_area_name: '' });
    setAddingArea(false);
  };
  const onArea = (id) => {
    const a = areas.find((x) => x.id === id);
    set({ area_id: id, area_name: a?.area_name || '' });
  };

  const areaOk = !!(form.area_id || (form.new_area_name && form.new_area_name.trim()));

  const selectedLocation = locations.find((l) => l.id === form.location_id) || null;
  const selectedLocationAddress = selectedLocation
    ? [selectedLocation.address, [selectedLocation.city, selectedLocation.state].filter(Boolean).join(', '), selectedLocation.zip].filter(Boolean).join(', ')
    : '';

  // Field-level validation keyed to the exact field so the UI can highlight it.
  const validate = (saveDraft) => {
    const e = {};
    if (!isClient && !locked && !form.customer_id) e.customer_id = 'Select the customer this work is for.';
    if (!form.location_id) e.location_id = 'Select the service location.';
    if (!saveDraft) {
      if (!areaOk) e.area = 'Select the service area/suite, or add a new one.';
      if (!form.title.trim()) e.title = 'Enter a short job title.';
      if (!form.description.trim()) e.description = 'Describe the work to be performed.';
      if (!form.service_scope) e.service_scope = 'Choose a service classification — Within or Above Contract Scope.';
      if (!isClient && !form.priority_level) e.priority_level = 'Assign an operational priority (P1–P5).';
      if (form.service_scope === 'Above Contract Scope') {
        if (!form.execution_authorization) e.execution_authorization = 'Select an authorization for above-scope work.';
        else if (form.execution_authorization === 'Authorized Up to NTE' && !(parseFloat(form.authorized_nte_amount) > 0))
          e.authorized_nte_amount = 'Enter a not-to-exceed amount greater than $0.';
      }
    } else {
      // Save Draft preserves incomplete work — only the record's identity is required.
      if (!form.title.trim()) e.title = 'Enter a short job title to save a draft.';
    }
    // Date relationship (mirror of the backend check) — only flagged when inconsistent.
    const recv = form.received_by_tfs_date || form.original_request_date;
    if (form.client_required_completion_date && recv) {
      const rd = new Date(`${String(form.client_required_completion_date).slice(0, 10)}T00:00:00`);
      const rv = new Date(`${String(recv).slice(0, 10)}T00:00:00`);
      if (!isNaN(rd) && !isNaN(rv) && rd < rv)
        e.client_required_completion_date = "Required completion date can't be before the requested/received date.";
    }
    return e;
  };

  const submit = async (saveDraft = false) => {
    const clientErrors = validate(saveDraft);
    if (Object.keys(clientErrors).length) {
      fe.setErrors(clientErrors);
      scrollToSummary();
      return;
    }
    fe.clearAll();
    setSubmitting(true);
    try {
      if (createWorkOrder) {
        const woPayload = {
          ...form,
          save_as_draft: saveDraft,
          client_idempotency_key: idemKeyRef.current,
        };
        const r = await axios.post(`${API}/work-orders/direct`, woPayload, authHeaders);
        toast.success(saveDraft ? 'Work order draft saved' : `Work Order ${r.data.tfs_work_order_number} created`);
        onWorkOrderCreated?.(r.data);
        return;
      }
      const payload = {
        ...form,
        authorized_nte_amount: form.authorized_nte_amount ? parseFloat(form.authorized_nte_amount) : null,
        save_as_draft: saveDraft,
        fulfillment_route: canRoute ? (form.fulfillment_route || 'TFS Operations') : null,
      };
      const r = await axios.post(`${API}/service-requests`, payload, authHeaders);
      toast.success(saveDraft ? `Draft ${r.data.request_number} saved` : `Service Request ${r.data.request_number} submitted`);
      onSubmitted?.(r.data);
    } catch (err) {
      const det = err.response?.data?.detail;
      if (det && typeof det === 'object' && det.field_errors) {
        // Map backend field names onto the form's field keys so each one highlights.
        const mapped = {};
        Object.entries(det.field_errors).forEach(([k, v]) => {
          mapped[k === 'required_service_date' ? 'client_required_completion_date' : k] = v;
        });
        fe.setErrors(mapped);
        scrollToSummary();
        toast.error('Please correct the highlighted fields.');
      } else if (err.response && !det) {
        toast.error('Connection problem — your entries are preserved. Please try again.');
      } else {
        toast.error((typeof det === 'string' && det) || (createWorkOrder ? 'Failed to create work order' : 'Failed to submit request'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="max-w-3xl border-slate-200 shadow-sm rounded-2xl overflow-hidden" data-testid="service-request-form">
      <CardHeader className="border-b border-slate-100 bg-slate-50/60 pb-5">
        <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#8FC93A]">Service Request</div>
        <CardTitle className="mt-1 text-2xl font-extrabold text-[#0E2A47]">{createWorkOrder ? 'Create New Work Order' : 'New Service Request'}</CardTitle>
        <p className="text-sm text-slate-500 mt-1">
          {isLocationRequester
            ? 'Tell us what needs attention. Your Facilities Director will review the request and determine the next steps.'
            : fdMode
            ? 'Create a portfolio service request and choose how it should be handled — send to TFS, route for internal review, mark for outside-provider sourcing, or record it for later.'
            : 'Internal: create a request on behalf of a customer. Recorded as entered by TFS.'}
        </p>
      </CardHeader>
      <CardContent className="space-y-7 pt-6">
        <div className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-400">Location</div>
        {/* Customer: derived from the authenticated account in client mode (not shown here — it's in the persistent banner). Proxy mode selects a customer. */}
        {!isClient && locked && (
          <div data-testid="sr-locked-customer">
            <Label>Customer</Label>
            <div className="mt-1 flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5">
              <span className="rounded bg-[#0E2A47] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">On Behalf</span>
              <span className="font-semibold text-[#0E2A47]">{form.customer_name}</span>
              <span className="ml-auto text-xs text-slate-400">Locked to active customer</span>
            </div>
          </div>
        )}
        {!isClient && !locked && (
          <div ref={fe.register('customer_id')}>
            <Label>Customer *</Label>
            <Select value={form.customer_id} onValueChange={onCustomer}>
              <SelectTrigger data-testid="sr-customer-select" className={fe.cls('customer_id')}><SelectValue placeholder="Select customer" /></SelectTrigger>
              <SelectContent>
                {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <FieldError message={fe.errors.customer_id} testid="sr-customer-error" />
          </div>
        )}

        <div className="grid sm:grid-cols-2 gap-4">
          <div ref={fe.register('location_id')}>
            <Label>Service Location *</Label>
            <Select value={form.location_id} onValueChange={onLocation} disabled={!isClient && !form.customer_id}>
              <SelectTrigger data-testid="sr-location-select" className={fe.cls('location_id')}>
                <SelectValue placeholder={isClient ? 'Select your location' : (form.customer_id ? 'Select location' : 'Select customer first')} />
              </SelectTrigger>
              <SelectContent>
                {locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.location_name}</SelectItem>)}
              </SelectContent>
            </Select>
            <FieldError message={fe.errors.location_id} testid="sr-location-error" />
          </div>
          <div ref={fe.register('area')}>
            <Label>Service Area / Suite *</Label>
            {!addingArea ? (
              <>
                <Select value={form.area_id} onValueChange={onArea} disabled={!form.location_id}>
                  <SelectTrigger data-testid="sr-area-select" className={fe.cls('area')}><SelectValue placeholder={!form.location_id ? 'Select a location first' : (areas.length ? 'Select or search a service area' : 'No saved areas yet — add one')} /></SelectTrigger>
                  <SelectContent>
                    {areas.map((a) => <SelectItem key={a.id} value={a.id}>{a.area_name}</SelectItem>)}
                  </SelectContent>
                </Select>
                {form.location_id && (
                  <button type="button" data-testid="sr-add-area-btn"
                    onClick={() => { setAddingArea(true); set({ area_id: '', area_name: '' }); }}
                    className="mt-1 text-[11px] font-semibold text-[#0E2A47] hover:underline">+ Add a new area for this location</button>
                )}
              </>
            ) : (
              <div className="space-y-1">
                <Input data-testid="sr-new-area-input" value={form.new_area_name}
                  onChange={(e) => set({ new_area_name: e.target.value })}
                  placeholder="e.g., Lobby Restroom" disabled={!form.location_id} />
                <div className="flex items-center gap-3">
                  <button type="button" data-testid="sr-cancel-area-btn"
                    onClick={() => { setAddingArea(false); set({ new_area_name: '' }); }}
                    className="text-[11px] font-semibold text-slate-500 hover:underline">Choose an existing area instead</button>
                  <span className="text-[11px] text-slate-400">Saved to this location after the request is created.</span>
                </div>
              </div>
            )}
            <FieldError message={fe.errors.area} testid="sr-area-error" />
          </div>
        </div>

        {/* Compact in-form confirmation of the chosen location + read-only client identifiers */}
        {selectedLocation && (
          <div className="-mt-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 flex items-start gap-3" data-testid="sr-location-confirm">
            <MapPin size={18} className="mt-0.5 shrink-0 text-[#8FC93A]" />
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-semibold text-[#0E2A47]">{selectedLocation.location_name}</span>
                {selectedLocation.location_code && (
                  <span className="rounded-md bg-white px-2 py-0.5 font-mono text-[11px] font-semibold text-slate-600 border border-slate-200" data-testid="sr-loc-internal-code">ROUT #{selectedLocation.location_code}</span>
                )}
              </div>
              <div className="text-sm text-slate-500 mt-0.5">{selectedLocationAddress || '—'}</div>
              {(selectedLocation.client_location_code || selectedLocation.client_atm_identifier) && (
                <div className="mt-2 flex flex-wrap gap-2" data-testid="sr-client-identifiers">
                  {selectedLocation.client_location_code && (
                    <span className="rounded-md bg-[#0E2A47]/5 border border-[#0E2A47]/15 px-2 py-0.5 text-[11px] font-semibold text-[#0E2A47]">
                      Client Location Code: <span className="font-mono">{selectedLocation.client_location_code}</span>
                    </span>
                  )}
                  {selectedLocation.client_atm_identifier && (
                    <span className="rounded-md bg-[#0E2A47]/5 border border-[#0E2A47]/15 px-2 py-0.5 text-[11px] font-semibold text-[#0E2A47]">
                      Site / ATM ID: <span className="font-mono">{selectedLocation.client_atm_identifier}</span>
                    </span>
                  )}
                  <span className="text-[10px] text-slate-400 self-center">Auto-filled · read-only</span>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="pt-1 text-xs font-semibold uppercase tracking-[0.15em] text-slate-400">Request Details</div>
        <div>
          <Label>{isClient ? 'Reported Urgency *' : 'Client-Reported Urgency *'}</Label>
          {isLocationRequester
            ? <p className="text-xs text-slate-400 mt-0.5">How the condition appears to you. Your Facilities Director confirms the formal priority.</p>
            : isClient
            ? <p className="text-xs text-slate-400 mt-0.5">Select the urgency that best reflects the condition.</p>
            : <p className="text-xs text-slate-400 mt-0.5">The client's perceived urgency — kept separate from the TFS operational priority you set below.</p>}
          <div className="flex gap-3 mt-1.5 max-w-md">
            <button type="button" data-testid="sr-type-standard"
              onClick={() => set({ request_type: 'Standard' })}
              className={`flex-1 rounded-xl border px-4 py-3 text-left transition-all ${form.request_type === 'Standard' ? 'border-[#0E2A47] bg-[#0E2A47] text-white shadow-sm' : 'border-slate-200 text-slate-600 hover:border-[#0E2A47]/40 hover:bg-slate-50'}`}>
              <div className="text-sm font-semibold">Standard</div>
              <div className={`text-[11px] mt-0.5 ${form.request_type === 'Standard' ? 'text-slate-300' : 'text-slate-400'}`}>Routine service need</div>
            </button>
            <button type="button" data-testid="sr-type-urgent"
              onClick={() => set({ request_type: 'Urgent' })}
              className={`flex-1 rounded-xl border px-4 py-3 text-left transition-all ${form.request_type === 'Urgent' ? 'border-amber-600 bg-amber-600 text-white shadow-sm' : 'border-slate-200 text-slate-600 hover:border-amber-300 hover:bg-amber-50/40'}`}>
              <div className="flex items-center gap-1.5 text-sm font-semibold"><AlertTriangle size={14} /> Urgent</div>
              <div className={`text-[11px] mt-0.5 ${form.request_type === 'Urgent' ? 'text-amber-100' : 'text-slate-400'}`}>Time-sensitive condition requiring prompt review</div>
            </button>
          </div>
        </div>

        {!isClient && (
          <div data-testid="sr-op-priority-block" ref={fe.register('priority_level')}>
            <Label>Operational Priority (TFS) *</Label>
            <p className="text-xs text-slate-400 mt-0.5">TFS operational classification — set by the ROUT administrator. Distinct from the client's reported urgency above.</p>
            <div className="max-w-md mt-1.5">
              <Select value={form.priority_level} onValueChange={(v) => set({ priority_level: v })}>
                <SelectTrigger data-testid="sr-op-priority-select" className={fe.cls('priority_level')}><SelectValue placeholder="Assign P1–P5" /></SelectTrigger>
                <SelectContent>
                  {PRIORITY_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <FieldError message={fe.errors.priority_level} testid="sr-priority-error" />
          </div>
        )}

        <div ref={fe.register('title')}>
          <Label>Title *</Label>
          <Input data-testid="sr-title-input" className={fe.cls('title')} value={form.title}
            onChange={(e) => set({ title: e.target.value })} placeholder="e.g., Restroom faucet leaking on 3rd floor" />
          <FieldError message={fe.errors.title} testid="sr-title-error" />
        </div>
        <div ref={fe.register('description')}>
          <Label>Description *</Label>
          <Textarea data-testid="sr-description-input" className={fe.cls('description')} rows={5} value={form.description}
            onChange={(e) => set({ description: e.target.value })} placeholder="Describe the issue, location details and any access notes." />
          <FieldError message={fe.errors.description} testid="sr-description-error" />
        </div>

        {/* ---- Service Classification (required) — mutually exclusive selection cards ---- */}
        <div className="pt-1 text-xs font-semibold uppercase tracking-[0.15em] text-slate-400">Service Classification *</div>
        <div ref={fe.register('service_scope')}>
        <div className={`grid sm:grid-cols-2 gap-3${fe.errors.service_scope ? ' rounded-xl ring-1 ring-red-400 bg-yellow-50 p-2' : ''}`} role="radiogroup" aria-label="Service Classification">
          <button type="button" data-testid="sr-scope-within" role="radio" aria-checked={form.service_scope === 'Within Contract Scope'}
            onClick={() => set({ service_scope: 'Within Contract Scope', execution_authorization: '', authorized_nte_amount: '' })}
            className={`relative rounded-xl px-4 py-3 pr-9 text-left transition-all outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-slate-500 ${form.service_scope === 'Within Contract Scope' ? 'border-2 border-slate-500 bg-slate-200 text-slate-900 shadow-sm' : 'border border-slate-200 text-slate-600 hover:border-slate-400 hover:bg-slate-50'}`}>
            {form.service_scope === 'Within Contract Scope' && (
              <span className="absolute top-2.5 right-2.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-slate-700 text-white" data-testid="sr-scope-within-check"><Check size={13} strokeWidth={3} /></span>
            )}
            <div className="text-sm font-bold">Within Contract Scope</div>
            <div className={`text-[11px] mt-0.5 ${form.service_scope === 'Within Contract Scope' ? 'text-slate-700' : 'text-slate-400'}`}>Covered by the current contract — no additional billing</div>
          </button>
          <button type="button" data-testid="sr-scope-above" role="radio" aria-checked={form.service_scope === 'Above Contract Scope'}
            onClick={() => set({ service_scope: 'Above Contract Scope' })}
            className={`relative rounded-xl px-4 py-3 pr-9 text-left transition-all outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-yellow-500 ${form.service_scope === 'Above Contract Scope' ? 'border-2 border-yellow-500 bg-yellow-100 text-yellow-950 shadow-sm' : 'border border-slate-200 text-slate-600 hover:border-yellow-300 hover:bg-yellow-50/50'}`}>
            {form.service_scope === 'Above Contract Scope' && (
              <span className="absolute top-2.5 right-2.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-yellow-600 text-white" data-testid="sr-scope-above-check"><Check size={13} strokeWidth={3} /></span>
            )}
            <div className="text-sm font-bold">Above Contract Scope</div>
            <div className={`text-[11px] mt-0.5 ${form.service_scope === 'Above Contract Scope' ? 'text-yellow-900' : 'text-slate-400'}`}>May require additional authorization or pricing</div>
          </button>
        </div>
          <FieldError message={fe.errors.service_scope} testid="sr-scope-error" />
        </div>

        {form.service_scope === 'Within Contract Scope' && (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-medium text-emerald-800" data-testid="sr-within-note">
            Covered under the current contract — no proposal or NTE required. It can still be scheduled, assigned, completed and audited.
          </div>
        )}

        {form.service_scope === 'Above Contract Scope' && (
          <>
            <div className="pt-1 text-xs font-semibold uppercase tracking-[0.15em] text-slate-400">Authorization *</div>
            <div ref={fe.register('execution_authorization')}>
            <div className={`grid sm:grid-cols-3 gap-3${fe.errors.execution_authorization ? ' rounded-xl ring-1 ring-red-400 bg-yellow-50 p-2' : ''}`}>
              <button type="button" data-testid="sr-exec-proposal"
                onClick={() => set({ execution_authorization: 'Proposal Required Before Execution', authorized_nte_amount: '' })}
                className={`rounded-xl border px-4 py-3 text-left transition-all ${form.execution_authorization === 'Proposal Required Before Execution' ? 'border-amber-600 bg-amber-600 text-white shadow-sm' : 'border-slate-200 text-slate-600 hover:border-amber-300 hover:bg-amber-50/40'}`}>
                <div className="text-sm font-semibold">Proposal Required Before Execution</div>
                <div className={`text-[11px] mt-0.5 ${form.execution_authorization === 'Proposal Required Before Execution' ? 'text-amber-100' : 'text-slate-400'}`}>Blocks work order until an accepted proposal is recorded</div>
              </button>
              <button type="button" data-testid="sr-exec-nte"
                onClick={() => set({ execution_authorization: 'Authorized Up to NTE' })}
                className={`rounded-xl border px-4 py-3 text-left transition-all ${form.execution_authorization === 'Authorized Up to NTE' ? 'border-[#0E2A47] bg-[#0E2A47] text-white shadow-sm' : 'border-slate-200 text-slate-600 hover:border-[#0E2A47]/40 hover:bg-slate-50'}`}>
                <div className="text-sm font-semibold">Authorized Up to NTE</div>
                <div className={`text-[11px] mt-0.5 ${form.execution_authorization === 'Authorized Up to NTE' ? 'text-slate-300' : 'text-slate-400'}`}>Proceed up to a not-to-exceed amount</div>
              </button>
              <button type="button" data-testid="sr-exec-preapproved"
                onClick={() => set({ execution_authorization: 'Pre-Approved to Proceed', authorized_nte_amount: '', authorization_method: '', authorized_by_email: '' })}
                className={`rounded-xl border px-4 py-3 text-left transition-all ${form.execution_authorization === 'Pre-Approved to Proceed' ? 'border-[#0E2A47] bg-[#0E2A47] text-white shadow-sm' : 'border-slate-200 text-slate-600 hover:border-[#0E2A47]/40 hover:bg-slate-50'}`}>
                <div className="text-sm font-semibold">Pre-Approved to Proceed</div>
                <div className={`text-[11px] mt-0.5 ${form.execution_authorization === 'Pre-Approved to Proceed' ? 'text-slate-300' : 'text-slate-400'}`}>Authorized — may proceed to work order</div>
              </button>
            </div>
            <FieldError message={fe.errors.execution_authorization} testid="sr-exec-error" />
            </div>
            {form.execution_authorization === 'Authorized Up to NTE' && (
              <div className="max-w-xs" data-testid="sr-nte-block" ref={fe.register('authorized_nte_amount')}>
                <Label>NTE Amount ($) *</Label>
                <Input type="number" min="0" step="0.01" data-testid="sr-nte-input" className={fe.cls('authorized_nte_amount')} value={form.authorized_nte_amount}
                  onChange={(e) => set({ authorized_nte_amount: e.target.value })} placeholder="e.g., 1500" />
                {form.authorized_nte_amount !== '' && parseFloat(form.authorized_nte_amount) <= 0 && (
                  <p className="text-xs text-red-600 mt-1">Enter a positive amount.</p>
                )}
                <FieldError message={fe.errors.authorized_nte_amount} testid="sr-nte-error" />
              </div>
            )}
            {form.execution_authorization === 'Pre-Approved to Proceed' && (
              <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3 space-y-2" data-testid="sr-preapproved-comments">
                <div className="text-[11px] text-slate-500">Pre-approved status satisfies authorization — no evidence required. We automatically record you and the time.</div>
                <Label>Comments <span className="font-normal text-slate-400">(optional)</span></Label>
                <Textarea data-testid="sr-preapproved-comments-input" rows={2} value={form.authorization_evidence_ref}
                  onChange={(e) => set({ authorization_evidence_ref: e.target.value })} placeholder="Add any notes (optional)" />
              </div>
            )}
            {form.execution_authorization === 'Proposal Required Before Execution' && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800" data-testid="sr-proposal-note">
                This request will be set to "Proposal Required" and cannot become a work order until an accepted proposal or authoritative client approval is recorded.
              </div>
            )}
          </>
        )}

        {/* ---- Handling / Routing (FD self-service or TFS proxy) ---- */}
        {canRoute && (
          <>
            <div className="pt-1 text-xs font-semibold uppercase tracking-[0.15em] text-slate-400">Handling / Routing *</div>
            <div className="max-w-md">
              <Select value={form.fulfillment_route} onValueChange={(v) => set({ fulfillment_route: v })}>
                <SelectTrigger data-testid="sr-route-select"><SelectValue placeholder="How should this be handled?" /></SelectTrigger>
                <SelectContent>
                  {['TFS Operations', 'Internal Review', 'Provider Selection Required', 'Unassigned / Record Only'].map((x) => (
                    <SelectItem key={x} value={x}>{x}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-400 mt-1">Only requests routed to <span className="font-semibold text-[#0E2A47]">TFS Operations</span> enter the TFS work queue. Others stay your records until you route them.</p>
            </div>
          </>
        )}

        {/* ---- Reference & Intake ---- */}
        <div className="pt-1 text-xs font-semibold uppercase tracking-[0.15em] text-slate-400">Reference &amp; Intake</div>

        {isClient ? (
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500" data-testid="sr-intake-portal">
            Intake source: <span className="font-semibold text-[#0E2A47]">Portal</span> · request and submission timestamps are recorded automatically.
          </div>
        ) : (
          <>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Intake Source</Label>
                <Select value={form.intake_source} onValueChange={(v) => set({ intake_source: v })}>
                  <SelectTrigger data-testid="sr-intake-source-select"><SelectValue placeholder="Select source" /></SelectTrigger>
                  <SelectContent>
                    {['Direct Client Request', 'Forwarded Client Email', 'Client Work-Order System', 'Phone', 'Other'].map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {form.intake_source === 'Client Work-Order System' && (
                <div>
                  <Label>Client System Name</Label>
                  <Input data-testid="sr-source-system-input" value={form.source_system}
                    onChange={(e) => set({ source_system: e.target.value })} placeholder="e.g., iOffice" />
                </div>
              )}
            </div>
            {form.intake_source === 'Client Work-Order System' && (
              <div className="max-w-md">
                <Label>Client Ticket / Work-Order Number <span className="text-slate-400 font-normal">(when applicable)</span></Label>
                <Input data-testid="sr-client-ticket-input" value={form.client_work_order_number}
                  onChange={(e) => set({ client_work_order_number: e.target.value })} placeholder="e.g., IO-448127" />
                <p className="text-[11px] text-slate-400 mt-1">A client work-order system does not replace the original requester — still record who requested it below.</p>
              </div>
            )}
            <div className="grid sm:grid-cols-2 gap-4">
              <div ref={fe.register('original_request_date')}>
                <Label>Original Request Date &amp; Time</Label>
                <Input type="datetime-local" data-testid="sr-original-date-input" className={fe.cls('original_request_date')} value={form.original_request_date}
                  onChange={(e) => set({ original_request_date: e.target.value })} />
                <FieldError message={fe.errors.original_request_date} testid="sr-original-date-error" />
              </div>
              <div ref={fe.register('received_by_tfs_date')}>
                <Label>Received by TFS Date &amp; Time</Label>
                <Input type="datetime-local" data-testid="sr-received-date-input" className={fe.cls('received_by_tfs_date')} value={form.received_by_tfs_date}
                  onChange={(e) => set({ received_by_tfs_date: e.target.value })} />
                <FieldError message={fe.errors.received_by_tfs_date} testid="sr-received-date-error" />
              </div>
            </div>
          </>
        )}

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Client Reference / Ticket # <span className="text-slate-400 font-normal">(optional)</span></Label>
            <Input data-testid="sr-client-ref-input" value={form.client_reference_number}
              onChange={(e) => set({ client_reference_number: e.target.value })} placeholder="Separate client or legacy reference" />
          </div>
          <div>
            <Label>Client PO # <span className="text-slate-400 font-normal">(optional)</span></Label>
            <Input data-testid="sr-client-po-input" value={form.client_po_number}
              onChange={(e) => set({ client_po_number: e.target.value })} placeholder="Optional" />
          </div>
        </div>

        <div className="max-w-xs" ref={fe.register('client_required_completion_date')}>
          <Label>Client-Required Completion Date</Label>
          <Input type="date" data-testid="sr-required-date-input" className={fe.cls('client_required_completion_date')} value={form.client_required_completion_date}
            onChange={(e) => set({ client_required_completion_date: e.target.value })} />
          <p className="text-[11px] text-slate-400 mt-1">Priority (P1–P5) is auto-derived from the interval to this date.</p>
          <FieldError message={fe.errors.client_required_completion_date} testid="sr-required-date-error" />
        </div>

        {!isClient && (
          <>
            <div className="pt-1 text-xs font-semibold uppercase tracking-[0.15em] text-slate-400">Request Chain — three distinct roles</div>
            <div className="rounded-lg border border-blue-200 bg-blue-50/60 px-3 py-2 text-[11px] text-blue-900" data-testid="sr-chain-help">
              Keep these separate: the <b>Original Requester</b> (who asked for the service), the <b>Client Authorizer / Forwarder</b> (who approved/forwarded it to TFS), and <b>Entered Into ROUT By</b> (you). Do not put the authorizer's name in the requester field.
            </div>

            {/* 1 — Original Requester */}
            <div className="text-[11px] font-bold uppercase tracking-wide text-[#0E2A47]">1 · Original Requester</div>
            {contacts.length > 0 && (
              <div>
                <Label>Pick from customer contacts <span className="text-slate-400 font-normal">(or enter manually)</span></Label>
                <select data-testid="sr-requester-contact-select" value={selectedContactId}
                  onChange={(e) => onPickContact(e.target.value)}
                  className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm text-slate-700">
                  <option value="">Choose the person who actually requested this…</option>
                  {contacts.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}{c.title ? ` — ${c.title}` : ''}{c.email ? ` · ${c.email}` : ''}{c.is_main ? ' (Main Contact)' : ''}</option>
                  ))}
                </select>
              </div>
            )}
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Requester Name</Label>
                <Input data-testid="sr-requester-name-input" value={form.requester_contact_name}
                  onChange={(e) => set({ requester_contact_name: e.target.value })} placeholder="e.g., Jeannie Costa" />
              </div>
              <div>
                <Label>Requester Title / Position</Label>
                <Input data-testid="sr-requester-title-input" value={form.requester_contact_title}
                  onChange={(e) => set({ requester_contact_title: e.target.value })} placeholder="e.g., Financial Specialist" />
              </div>
              <div>
                <Label>Requester Email</Label>
                <Input data-testid="sr-requester-email-input" value={form.requester_contact_email}
                  onChange={(e) => set({ requester_contact_email: e.target.value })} placeholder="e.g., jeanniecosta@synovus.com" />
              </div>
              <div>
                <Label>Requester Phone <span className="text-slate-400 font-normal">(optional)</span></Label>
                <Input data-testid="sr-requester-phone-input" value={form.requester_contact_phone}
                  onChange={(e) => set({ requester_contact_phone: e.target.value })} placeholder="Optional" />
              </div>
            </div>

            {/* 2 — Client Authorizer / Forwarder */}
            <div className="text-[11px] font-bold uppercase tracking-wide text-[#0E2A47] pt-1">2 · Client Authorizer / Forwarder <span className="font-normal normal-case text-slate-400">(when applicable)</span></div>
            {contacts.length > 0 && (
              <div>
                <Label>Pick from customer contacts <span className="text-slate-400 font-normal">(or enter manually)</span></Label>
                <select data-testid="sr-authorizer-contact-select" value={selectedAuthorizerId}
                  onChange={(e) => onPickAuthorizer(e.target.value)}
                  className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm text-slate-700">
                  <option value="">Choose the person who approved/forwarded it…</option>
                  {contacts.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}{c.title ? ` — ${c.title}` : ''}{c.email ? ` · ${c.email}` : ''}{c.is_main ? ' (Main Contact)' : ''}</option>
                  ))}
                </select>
              </div>
            )}
            <div className="grid sm:grid-cols-3 gap-4">
              <div>
                <Label>Authorized / Forwarded By</Label>
                <Input data-testid="sr-authorized-by-input" value={form.client_authorized_by}
                  onChange={(e) => set({ client_authorized_by: e.target.value })} placeholder="e.g., Jeff Beuttel" />
              </div>
              <div>
                <Label>Authorizer Title</Label>
                <Input data-testid="sr-authorizer-role-input" value={form.client_authorized_by_role}
                  onChange={(e) => set({ client_authorized_by_role: e.target.value })} placeholder="Leave blank if not provided" />
              </div>
              <div>
                <Label>Authorizer Email</Label>
                <Input data-testid="sr-authorizer-email-input" value={form.authorized_by_email}
                  onChange={(e) => set({ authorized_by_email: e.target.value })} placeholder="e.g., jeffbeuttel@synovus.com" />
              </div>
            </div>

            {/* 3 — Entered Into ROUT By (automatic) */}
            <div className="text-[11px] font-bold uppercase tracking-wide text-[#0E2A47] pt-1">3 · Entered Into ROUT By</div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600" data-testid="sr-entered-by-note">
              Automatically recorded as <span className="font-semibold text-[#0E2A47]">{currentUserName || 'the signed-in TFS user'}</span> — this cannot be changed.
            </div>
          </>
        )}

        <div ref={summaryRef}>
          <CorrectionSummary errors={fe.errors} labels={FIELD_LABELS} onJump={fe.jumpTo} />
        </div>

        <div className="flex flex-wrap justify-end gap-3 pt-3 border-t border-slate-100 -mx-6 px-6">
          {onCancel && <Button variant="outline" onClick={onCancel} data-testid="sr-cancel-btn">Cancel</Button>}
          {canRoute && (
            <Button variant="outline" onClick={() => submit(true)} disabled={submitting}
              className="border-[#0E2A47]/30 text-[#0E2A47] hover:bg-slate-50" data-testid="sr-save-draft-btn">
              {submitting ? 'Saving…' : 'Save as Draft'}
            </Button>
          )}
          <Button onClick={() => submit(false)} disabled={submitting}
            className="bg-[#0E2A47] hover:bg-[#16324F]" data-testid="sr-submit-btn">
            {submitting ? (createWorkOrder ? 'Creating…' : 'Submitting…') : (createWorkOrder ? 'Create Work Order' : 'Submit Request')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default ServiceRequestForm;
